import React from 'react'
import { Link } from 'react-router-dom'
import { useNavigationStore } from '@/stores/navigation'
import { useCookieConsentStore } from '@/stores/cookieConsent'
import { ROUTES, SOCIAL_LINKS } from '@/utils/constants'

const Footer: React.FC = () => {
  const brandGreen = "#7ed957"
  const footerBg = "#274060"
  const navigationStore = useNavigationStore()
  const cookieConsentStore = useCookieConsentStore()

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="space-y-3">
      <h4 className="text-base font-semibold">{title}</h4>
      <ul className="space-y-2 text-sm">
        {children}
      </ul>
    </div>
  )

  const Item = ({ href = "#", onClick, children }: { href?: string; onClick?: (e: React.MouseEvent) => void; children: React.ReactNode }) => (
    <li>
      {href === "#" ? (
        <a href={href} onClick={onClick} className="text-blue-100/90 hover:text-white transition">{children}</a>
      ) : (
        <Link to={href} onClick={onClick} className="text-blue-100/90 hover:text-white transition">{children}</Link>
      )}
    </li>
  )

  const navigationSections = [
    {
      title: "Products",
      items: [
        { name: "eceptionistCX", href: ROUTES.SERVICE_SUMMARY },
        { name: "referralCX", href: ROUTES.PRODUCTS.REFERRAL_CX },
        { name: "schedulingCX", href: ROUTES.PRODUCTS.SCHEDULING_CX },
        { name: "cellCX", href: ROUTES.PRODUCTS.CELL_CX },
        { name: "caseCX", href: ROUTES.PRODUCTS.CASE_CX },
        { name: "eConsultCX", href: ROUTES.PRODUCTS.ECONSULT_CX },
        { name: "probationCX", href: ROUTES.PRODUCTS.PROBATION_CX },
        { name: "vaxManagerCX", href: ROUTES.PRODUCTS.VAX_MANAGER_CX },
        { name: "consumerCX", href: ROUTES.PRODUCTS.CONSUMER_CX },
      ],
    },
    {
      title: "Solutions",
      items: [
        { name: "Hospitals & Health Systems", href: ROUTES.WHO_WE_SERVE },
        { name: "Cell Therapy & Research Centers", href: ROUTES.WHO_WE_SERVE },
        { name: "Specialty Clinics", href: ROUTES.WHO_WE_SERVE },
        { name: "Government Health Agencies", href: ROUTES.WHO_WE_SERVE },
        { name: "Probation & Justice", href: ROUTES.WHO_WE_SERVE },
        { name: "Employers & Retail Care", href: ROUTES.WHO_WE_SERVE },
      ],
    },
    {
      title: "Company",
      items: [
        { name: "About Us", href: ROUTES.ABOUT },
        { name: "Contact", href: "#" },
      ],
    },
    {
      title: "Resources",
      items: [
        { name: "Case Studies", href: ROUTES.USE_CASE },
        { name: "White Papers", href: ROUTES.WHITE_PAPERS },
        { name: "Blog", href: ROUTES.BLOG },
        { name: "ROI Calculator", href: ROUTES.ROI },
        { name: "Support", href: "#" },
      ],
    },
  ]

  return (
    <footer id="footer" style={{ background: footerBg }} className="text-white mt-1" role="contentinfo">
      {/* Desktop / Tablet */}
      <div className="hidden md:block">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-6 grid grid-cols-2 sm:grid-cols-4 gap-8">
          {navigationSections.map((section, index) => (
            <div key={section.title}>
              <Section title={section.title}>
                {section.items.map((item) => (
                  <Item 
                    key={item.name}
                    href={item.href} 
                    onClick={item.name === "Contact" ? (e) => { e.preventDefault(); navigationStore.openContactModal(); } : 
                           item.name === "Support" ? (e) => { e.preventDefault(); navigationStore.openSupportModal(); } : undefined}
                  >
                    {item.name}
                  </Item>
                ))}
              </Section>
              {index === 3 && (
                <button
                  onClick={navigationStore.openDemoModal}
                  className="inline-block mt-4 font-bold rounded-lg border border-black/10 px-4 py-2"
                  style={{ background: brandGreen, color: "#0e2110" }}
                >
                  Book a Demo
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile accordion */}
      <div className="md:hidden">
        <div className="max-w-6xl mx-auto px-4 pt-8 pb-4 space-y-2">
          {navigationSections.map((g) => (
            <details key={g.title} className="border-t border-white/20 py-2">
              <summary className="cursor-pointer select-none py-2 text-base font-semibold list-none flex items-center justify-between" aria-expanded="false">
                {g.title}
                <span className="text-white/70" aria-hidden="true">▼</span>
              </summary>
              <ul className="ml-1 pl-1 space-y-2 text-sm">
                {g.items.map((item) => {
                  const isContact = item.name === "Contact"
                  const isSupport = item.name === "Support"
                  
                  return (
                    <li key={item.name}>
                      {item.href === "#" ? (
                        <a 
                          href={item.href} 
                          onClick={isContact ? (e) => { e.preventDefault(); navigationStore.openContactModal(); } : 
                                 isSupport ? (e) => { e.preventDefault(); navigationStore.openSupportModal(); } : undefined}
                          className="text-blue-100/90 hover:text-white transition"
                        >
                          {item.name}
                        </a>
                      ) : (
                        <Link 
                          to={item.href} 
                          onClick={isContact ? (e) => { e.preventDefault(); navigationStore.openContactModal(); } : 
                                 isSupport ? (e) => { e.preventDefault(); navigationStore.openSupportModal(); } : undefined}
                          className="text-blue-100/90 hover:text-white transition"
                        >
                          {item.name}
                        </Link>
                      )}
                    </li>
                  )
                })}
              </ul>
            </details>
          ))}
          <button
            onClick={navigationStore.openDemoModal}
            className="inline-block mt-2 font-bold rounded-lg border border-black/10 px-4 py-2"
            style={{ background: brandGreen, color: "#0e2110" }}
          >
            Book a Demo
          </button>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/20">
        <div className="max-w-6xl mx-auto px-6 py-4 text-center text-sm text-blue-100/90 space-y-2">
          <div className="space-x-2">
            <a href={SOCIAL_LINKS.LINKEDIN} target="_blank" rel="noopener noreferrer" style={{ color: brandGreen }}>LinkedIn</a>
            <span className="text-white/40">|</span>
            <a href={SOCIAL_LINKS.X} target="_blank" rel="noopener noreferrer" style={{ color: brandGreen }}>X</a>
          </div>
          <div>© 2025 <span className="font-semibold">E-Ceptionist, Inc.</span> All rights reserved.</div>
          <div className="space-x-2">
            <button 
              onClick={navigationStore.openPrivacyPolicyModal}
              style={{ color: brandGreen }}
              className="hover:underline"
            >
              Privacy Policy
            </button>
            <span className="text-white/40">|</span>
            <button 
              onClick={navigationStore.openTermsOfServiceModal}
              style={{ color: brandGreen }}
              className="hover:underline"
            >
              Terms of Service
            </button>
            <span className="text-white/40">|</span>
            <button 
              onClick={navigationStore.openDataResidencyModal}
              style={{ color: brandGreen }}
              className="hover:underline"
            >
              Data Residency
            </button>
            <span className="text-white/40">|</span>
            <button 
              onClick={navigationStore.openAccessibilityModal}
              style={{ color: brandGreen }}
              className="hover:underline"
            >
              Accessibility
            </button>
            <span className="text-white/40">|</span>
            <button 
              onClick={cookieConsentStore.showPreferencesPanelAction}
              style={{ color: brandGreen }}
              className="hover:underline"
            >
              Cookie Settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer


